import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send, CheckCircle } from 'lucide-react';
import { api } from '../config/api';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');
    try {
      const response = await api.post('/contact.php', formData);
      if (response.data.success) {
        setSuccess(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setTimeout(() => setSuccess(false), 5000);
      } else {
        setErrorMsg(response.data.message || 'Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Contact error:', error);
      setErrorMsg('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      {/* Contact Header */}
      <section className="section-padding text-center" style={{
        background: 'linear-gradient(rgba(26, 26, 26, 0.8), rgba(26, 26, 26, 0.9)), url("https://images.unsplash.com/photo-1552566626-52f8b828add9?w=1920&q=80")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed',
        color: '#ffffff'
      }}>
        <div className="container">
          <span className="section-subtitle" style={{ color: '#D4AF37' }}>Get in Touch</span>
          <h1 className="mt-3 mb-4" style={{ fontFamily: 'Playfair Display, serif', fontSize: '3.5rem', fontWeight: 600, color: '#ffffff' }}>
            Contact Us
          </h1>
          <div className="gold-divider-center" style={{ background: '#D4AF37' }}></div>
          <p style={{ fontSize: '1.1rem', maxWidth: '600px', margin: '20px auto 0', lineHeight: 1.8, color: '#ffffff' }}>
            We'd love to hear from you. Reach out for reservations, inquiries, or just to say hello.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="section-padding-sm" style={{ backgroundColor: '#faf8f5' }}>
        <div className="container">
          <div className="row g-4 mb-5">
            {[
              { icon: <MapPin size={24} />, title: 'Address', lines: ['Seguku, Entebbe Road', 'Uganda'] },
              { icon: <Phone size={24} />, title: 'Call Us', lines: ['+256 772 206129', '+256 700 682587'], link: 'tel:+256772206129' },
              { icon: <Mail size={24} />, title: 'Email', lines: ['info@syrafoodpoint.com'], link: 'mailto:info@syrafoodpoint.com' },
              { icon: <Clock size={24} />, title: 'Hours', lines: ['Mon–Fri: 10AM – 10PM', 'Sat–Sun: 9AM – 11PM'] },
            ].map((item, index) => (
              <div key={index} className="col-md-6 col-lg-3">
                <div className="text-center p-4 h-100 hover-lift" style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '16px',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  border: '1px solid rgba(212, 175, 55, 0.08)'
                }}>
                  <div className="d-inline-flex align-items-center justify-content-center mb-3"
                    style={{
                      width: '56px', height: '56px', borderRadius: '50%',
                      backgroundColor: 'rgba(212, 175, 55, 0.1)', color: '#D4AF37'
                    }}>
                    {item.icon}
                  </div>
                  <h5 style={{ fontFamily: 'Playfair Display, serif', fontWeight: 600, fontSize: '1.05rem', marginBottom: '10px' }}>
                    {item.title}
                  </h5>
                  {item.lines.map((line, i) => (
                    <p key={i} className="mb-1" style={{ fontSize: '0.88rem', color: '#777' }}>
                      {item.link ? (
                        <a href={item.link} className="text-decoration-none" style={{ color: '#777' }}>{line}</a>
                      ) : line}
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Message Form + Map */}
          <div className="row g-4">
            <div className="col-lg-5">
              <div className="p-5 h-100" style={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)'
              }}>
                <h2 className="mb-4" style={{ fontFamily: 'Playfair Display, serif', fontSize: '1.8rem', fontWeight: 600 }}>
                  Send a Message
                </h2>

                <form onSubmit={handleSubmit}>
                  {success && (
                    <div style={{
                      backgroundColor: 'rgba(76,175,80,0.12)', color: '#2E7D32', padding: '14px 18px',
                      borderRadius: '10px', fontSize: '0.9rem', marginBottom: '20px',
                      display: 'flex', alignItems: 'center', gap: '8px'
                    }}>
                      <CheckCircle size={18} />
                      Message sent successfully! We'll get back to you soon.
                    </div>
                  )}
                  {errorMsg && (
                    <div style={{
                      backgroundColor: 'rgba(231,76,60,0.12)', color: '#C62828', padding: '14px 18px',
                      borderRadius: '10px', fontSize: '0.9rem', marginBottom: '20px', textAlign: 'center'
                    }}>
                      {errorMsg}
                    </div>
                  )}
                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#444' }}>Your Name</label>
                    <input type="text" className="form-control" name="name" value={formData.name} onChange={handleChange} required placeholder="John Doe" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#444' }}>Email Address</label>
                    <input type="email" className="form-control" name="email" value={formData.email} onChange={handleChange} required placeholder="john@example.com" />
                  </div>
                  <div className="mb-3">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#444' }}>Subject</label>
                    <input type="text" className="form-control" name="subject" value={formData.subject} onChange={handleChange} required placeholder="How can we help?" />
                  </div>
                  <div className="mb-4">
                    <label className="form-label" style={{ fontWeight: 600, fontSize: '0.85rem', color: '#444' }}>Message</label>
                    <textarea className="form-control" name="message" value={formData.message} onChange={handleChange} rows="5" required placeholder="Tell us more..." style={{ resize: 'none' }}></textarea>
                  </div>
                  <button type="submit" disabled={submitting} className="btn btn-gold d-flex align-items-center gap-2" style={{ padding: '14px 32px', fontSize: '0.85rem' }}>
                    <Send size={16} />
                    {submitting ? 'Sending...' : 'Send Message'}
                  </button>
                </form>

                {/* Social */}
                <div className="mt-4 pt-4" style={{ borderTop: '1px solid #f0f0f0' }}>
                  <p style={{ fontSize: '0.85rem', color: '#999', marginBottom: '12px' }}>Follow us</p>
                  <div className="d-flex gap-2">
                    <a href="https://www.facebook.com/syrafoodpoint" target="_blank" rel="noopener noreferrer" className="social-icon" style={{ width: '36px', height: '36px', fontSize: '0.85rem' }}><i className="fab fa-facebook-f"></i></a>
                    <a href="https://www.instagram.com/syrafoodpoint" target="_blank" rel="noopener noreferrer" className="social-icon" style={{ width: '36px', height: '36px', fontSize: '0.85rem' }}><i className="fab fa-instagram"></i></a>
                    <a href="https://www.tiktok.com/@syrafoodpoint" target="_blank" rel="noopener noreferrer" className="social-icon" style={{ width: '36px', height: '36px', fontSize: '0.85rem' }}><i className="fab fa-tiktok"></i></a>
                    <a href="https://wa.me/256772206129" target="_blank" rel="noopener noreferrer" className="social-icon" style={{ width: '36px', height: '36px', fontSize: '0.85rem' }}><i className="fab fa-whatsapp"></i></a>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-7">
              <div style={{ borderRadius: '16px', overflow: 'hidden', height: '100%', minHeight: '500px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d15956.123456789!2d32.55!3d0.25!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMMKwMTUnMDAuMCJOIDMywrAzMycwMC4wIkU!5e0!3m2!1sen!2sug!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0, minHeight: '500px' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Syra Food Point Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
